import { ValueOf } from './state.enum';

/**
 * Stato di CHIUSURA di una sessione, distinto dallo stato della sessione stessa.
 *
 * Nasce da una domanda che il modello non sapeva porre: "questa sessione è finita, ma i suoi dati
 * sono definitivi?". Prima `endedAt` rispondeva a tutte e due le domande insieme — chiusa
 * operativamente E fatturabile — e quando una colonnina si guasta le due cose divergono: la
 * sessione deve chiudersi subito (l'utente non può restare bloccato), ma i dati con cui fatturarla
 * potrebbero arrivare ore dopo, o non arrivare affatto.
 *
 * NULLABLE per scelta: una sessione chiusa normalmente NON ha questo campo valorizzato. Il percorso
 * sano resta identico a prima (chiusura = consolidamento immediato) e non serve backfill.
 * Il campo esiste solo per le sessioni che hanno qualcosa da dire in più.
 */
export const SessionClosure = Object.freeze({
  /**
   * Chiusa OPERATIVAMENTE ma non consolidata: la colonnina si è guastata o è diventata
   * irraggiungibile durante la sessione. Il connettore è libero e l'utente può ricaricare altrove,
   * ma i conteggi NON sono definitivi e `session.ended` non è ancora stato pubblicato — quindi
   * nessun CDR, nessun addebito. Si resta qui per la finestra di riconciliazione.
   */
  ANOMALOUS: 'ANOMALOUS',

  /**
   * Finestra di riconciliazione chiusa: i dati sono definitivi. Ci si arriva in tre modi —
   * è arrivato lo StopTransaction tardivo (dati reali), la colonnina ha completato la transazione,
   * oppure la finestra è scaduta e si conferma l'ultimo dato attendibile. Da qui parte
   * `session.ended` e quindi CDR e addebito.
   */
  CONSOLIDATED: 'CONSOLIDATED',
} as const);

export type SessionClosureValues = ValueOf<typeof SessionClosure>;

const CLOSURE_SET: ReadonlySet<string> = new Set(Object.values(SessionClosure));

export function parseSessionClosure(value: unknown): SessionClosureValues | undefined {
  return typeof value === 'string' && CLOSURE_SET.has(value) ? (value as SessionClosureValues) : undefined;
}

/**
 * Una sessione è pronta per il consolidamento economico (CDR + addebito)?
 *
 * `undefined`/`null` = chiusura normale → sì, come è sempre stato. `ANOMALOUS` = no, si aspetta.
 * È l'unica regola che decide se `session.ended` parte, e vive qui perché la leggono sia il
 * publisher del backend sia chi consolida a mano dal backoffice.
 */
export function isEconomicallyConsolidable(closure: SessionClosureValues | null | undefined): boolean {
  return closure !== SessionClosure.ANOMALOUS;
}

/**
 * Confine della finestra FATTURABILE. `billableUntil` esiste solo quando è diverso da `endedAt`:
 * lo valorizza la chiusura anomala con l'ultimo istante in cui la colonnina era osservabile.
 *
 * Il senso: se la CP si guasta alle 10:00 e la sessione si chiude operativamente alle 10:05, il
 * parcheggio maturato fino alle 10:00 è reale e si fattura; i 5 minuti di cecità no. Prendere
 * `endedAt` fatturerebbe anche quelli, prendere `stoppedAt` regalerebbe tutto il maturato.
 */
export function billableEnd(
  endedAt: Date | string | null | undefined,
  billableUntil?: Date | string | null,
): Date | undefined {
  const ended = endedAt ? new Date(endedAt) : undefined;
  const billable = billableUntil ? new Date(billableUntil) : undefined;
  if (!billable || Number.isNaN(billable.getTime())) return ended;
  if (!ended || Number.isNaN(ended.getTime())) return billable;
  // Mai OLTRE la chiusura: `billableUntil` può solo restringere la finestra, mai allungarla.
  return billable < ended ? billable : ended;
}
